import React, { useState, useEffect, useRef } from 'react';
import Swal from "sweetalert2";
import BotonManualHeader from "../../components/BotonManualHeader";
import { Container, Card, Form, Spinner, Table, Badge, Button, Row, Col, InputGroup } from 'react-bootstrap';
import { getFirestore, collection, addDoc, setDoc, doc, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import firebaseApp from "../../firebase/credenciales";
import * as pdfjsLib from 'pdfjs-dist';
import { 
  FaArrowUp, 
  FaArrowDown, 
  FaArrowLeft, 
  FaArrowRight, 
  FaSave, 
  FaTrash, 
  FaSearch, 
  FaChevronLeft, 
  FaChevronRight, 
  FaPlus, 
  FaMinus, 
  FaFilePdf, 
  FaTimes, 
  FaCrosshairs,
  FaInfoCircle,
  FaSignature
} from 'react-icons/fa';

const db = getFirestore(firebaseApp);

const PDFSignatureDetector = ({ usuario }) => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [signatures, setSignatures] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [searchPattern, setSearchPattern] = useState('_'.repeat(30));
  const [documentType, setDocumentType] = useState('Boletas de pago');
  const [savedCoordinates, setSavedCoordinates] = useState([]);
  const ruc = usuario.ruc;
  const [savingCoordinates, setSavingCoordinates] = useState(false);
  const [onboardingDocs, setOnboardingDocs] = useState([]);
  
  // New States for Advanced Features
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.3);
  const [activeSignature, setActiveSignature] = useState(null);
  const [moveStep, setMoveStep] = useState(5);

  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);

  // Document types options
  const documentTypes = [
    { value: 'Boletas de pago', label: 'BOLETAS DE PAGO' },
    { value: 'CTS', label: 'CTS' },
    { value: 'Gratificaciones', label: 'GRATIFICACIONES' },
    { value: 'Utilidades', label: 'UTILIDADES' },
    { value: 'Contratos', label: 'CONTRATOS' },
    { value: 'T-Registro', label: 'T-REGISTRO' },
    { value: 'Política Salarial', label: 'POLÍTICA SALARIAL' },
    { value: 'SST', label: 'SST' },
    { value: 'Renta', label: 'RENTA DE 5TA' },
    { value: 'Memorandums', label: 'MEMORANDUMS' },
    { value: 'Vacaciones', label: 'VACACIONES' },
    { value: 'Prestamos', label: 'PRÉSTAMOS' },
    { value: 'Adelantos', label: 'ADELANTOS' },
    { value: 'reciboHonorarios', label: 'RECIBO POR HONORARIOS' },
    { value: 'ConstanciaFirma', label: 'CONSTANCIA DE FIRMA' },
    { value: 'PoliticasLaborales', label: 'POLÍTICAS LABORALES' },
    { value: 'Ceses', label: 'CESES Y LIQUIDACIONES' },
    { value: 'Otros', label: 'OTROS' }
  ];

  // Fetch saved coordinates
  const fetchSavedCoordinates = async () => {
    try {
      const firmasRef = collection(db, 'firmasParametros');
      const q = query(firmasRef, where("rucEmpresa", "==", ruc));
      const querySnapshot = await getDocs(q);
      const coordinates = querySnapshot.docs.map(doc => doc.data());
      setSavedCoordinates(coordinates);
    } catch (error) {
      console.error("Error al obtener coordenadas:", error);
    }
  };

  // Fetch onboarding documents from onboarding_lista/RUC/documentos
  const fetchOnboardingDocs = async () => {
    try {
      const docsRef = collection(db, `onboarding_lista/${ruc}/documentos`);
      const querySnapshot = await getDocs(docsRef);
      let docsList = [];
      
      querySnapshot.forEach(doc => {
        const data = doc.data();
        // Filter: type is 'sign' and active is true
        if (data.type === 'sign' && data.activo === true) {
          const docTitle = data.title || data.nombre || doc.id;
          docsList.push({
            value: docTitle,
            label: docTitle.toUpperCase()
          });
        }
      });
      
      // If collection is empty, load the standard predefined signable onboarding documents as fallback
      if (docsList.length === 0) {
        const defaultSignableOnboarding = [
          "Declaración Jurada de Domicilio",
          "Declaración Jurada de Quinta Categoría o certificado de 5ta",
          "Declaración Jurada de apertura para pago de Remuneración",
          "Convenio de Confidencialidad",
          "Autorización para uso de datos personales",
          "Consentimiento Informado (realización de exámenes médicos)",
          "Compromiso de Responsabilidad con los bienes",
          "Carta de Compromiso (Vigilancia de salud)",
          "Declaración jurada de beneficiarios - Vida Ley",
          "Convenio de entrega Digital de Documentos Laborales",
          "Declaración de Exoneración de responsabilidad"
        ];
        
        docsList = defaultSignableOnboarding.map(name => ({
          value: name,
          label: name.toUpperCase()
        }));
      }
      
      setOnboardingDocs(docsList);
    } catch (error) {
      console.error("Error al obtener documentos de onboarding:", error);
      // Fallback predefined onboarding list in case of error/permissions
      const defaultSignableOnboarding = [
        "Declaración Jurada de Domicilio",
        "Declaración Jurada de Quinta Categoría o certificado de 5ta",
        "Declaración Jurada de apertura para pago de Remuneración",
        "Convenio de Confidencialidad",
        "Autorización para uso de datos personales",
        "Consentimiento Informado (realización de exámenes médicos)",
        "Compromiso de Responsabilidad con los bienes",
        "Carta de Compromiso (Vigilancia de salud)",
        "Declaración jurada de beneficiarios - Vida Ley",
        "Convenio de entrega Digital de Documentos Laborales",
        "Declaración de Exoneración de responsabilidad"
      ];
      setOnboardingDocs(defaultSignableOnboarding.map(name => ({
        value: name,
        label: name.toUpperCase()
      })));
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setInitialLoading(true);
      try {
        await Promise.all([
          fetchSavedCoordinates(),
          fetchOnboardingDocs()
        ]);
      } finally {
        setInitialLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line
  }, []);

  // Configure PDF.js Worker
  useEffect(() => {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
  }, []);

  // Render PDF page and draw signature boxes on Canvas
  useEffect(() => {
    let active = true;

    const renderPage = async () => {
      if (!pdfDoc) return;
      try {
        const page = await pdfDoc.getPage(currentPage);
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas || !active) return;
        
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        // Cancel running render tasks to avoid overlay glitches
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
        }

        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };
        
        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;

        await renderTask.promise;
        renderTaskRef.current = null;

        if (!active) return;

        // 1. Draw Auto-detected signatures
        signatures
          .filter(sig => sig.page === currentPage)
          .forEach((sig, index) => {
            // Check if this signature is currently active for editing
            const isEditing = activeSignature && activeSignature.source === 'auto' && activeSignature.index === index;
            if (isEditing) return; // Skip rendering it as yellow, it will render as active/green

            context.save();
            context.globalAlpha = 0.35;
            context.fillStyle = '#ffc107'; // yellow
            context.strokeStyle = '#ff9800'; // orange
            context.lineWidth = 1.5;
            context.setLineDash([4, 4]);

            // PDF.js uses bottom-left origin. We convert to viewport (canvas) coordinates.
            // In PDF, top-left of text is (sig.x, sig.y + sig.height)
            const [vx, vy] = viewport.convertToViewportPoint(sig.x, sig.y + sig.height);
            const vw = sig.width * scale;
            const vh = sig.height * scale;

            context.fillRect(vx, vy, vw, vh);
            context.strokeRect(vx, vy, vw, vh);

            // Label
            context.globalAlpha = 0.8;
            context.fillStyle = '#d35400';
            context.font = '11px sans-serif';
            context.fillText(`Detectada #${index + 1}`, vx + 2, vy - 4);
            context.restore();
          });

        // 2. Draw Active/Selected Signature (Manual, Auto-active, or Selected Saved)
        if (activeSignature && activeSignature.page === currentPage) {
          context.save();
          context.globalAlpha = 0.45;
          
          const isSaved = activeSignature.source === 'saved';
          context.fillStyle = isSaved ? '#0d6efd' : '#198754'; // blue for saved, green for new/manual
          context.strokeStyle = isSaved ? '#0a58ca' : '#0f5132';
          context.lineWidth = 2.5;

          const [vx, vy] = viewport.convertToViewportPoint(activeSignature.x, activeSignature.y + activeSignature.height);
          const vw = activeSignature.width * scale;
          const vh = activeSignature.height * scale;

          context.fillRect(vx, vy, vw, vh);
          context.strokeRect(vx, vy, vw, vh);

          // Draw anchors on corners for premium feel
          context.globalAlpha = 0.9;
          context.fillStyle = isSaved ? '#0a58ca' : '#0f5132';
          const dotSize = 6;
          context.fillRect(vx - dotSize/2, vy - dotSize/2, dotSize, dotSize);
          context.fillRect(vx + vw - dotSize/2, vy - dotSize/2, dotSize, dotSize);
          context.fillRect(vx - dotSize/2, vy + vh - dotSize/2, dotSize, dotSize);
          context.fillRect(vx + vw - dotSize/2, vy + vh - dotSize/2, dotSize, dotSize);

          // Label
          context.font = 'bold 12px sans-serif';
          let label = "Firma Activa";
          if (activeSignature.source === 'manual') label = "Firma Manual (Ajustable)";
          if (activeSignature.source === 'auto') label = `Ajustando Firma #${activeSignature.index + 1}`;
          if (activeSignature.source === 'saved') label = `Firma Registrada (${activeSignature.tipo})`;
          
          context.fillText(label, vx + 2, vy - 6);
          context.restore();
        }

      } catch (err) {
        if (err.name !== 'RenderingCancelledException') {
          console.error("Error al renderizar página del PDF:", err);
        }
      }
    };

    renderPage();

    return () => {
      active = false;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
    };
  }, [pdfDoc, currentPage, scale, signatures, activeSignature, documentType]);

  // Handle PDF file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setLoading(true);
    setSignatures([]);
    setActiveSignature(null);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      setPdfDoc(pdf);
      setNumPages(pdf.numPages);
      setCurrentPage(1);
      await findSignatureLines(pdf);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al procesar el PDF: ' + err.message
      });
    } finally {
      setLoading(false);
    }
  };

  // Automated search for signature lines (underscores)
  const findSignatureLines = async (pdf) => {
    const signaturePositions = [];
    if (!searchPattern) return;

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      textContent.items.forEach(item => {
        const text = item.str;
        if (text.includes(searchPattern)) {
          const [x, y] = item.transform.slice(4, 6);
          signaturePositions.push({
            page: pageNum,
            text: text,
            underscoreCount: searchPattern.length,
            x: Math.round(x),
            y: Math.round(y),
            width: Math.round(item.width),
            height: Math.round(item.height)
          });
        }
      });
    }
    setSignatures(signaturePositions);
    
    if (signaturePositions.length > 0) {
      // Automatically select first found signature
      setActiveSignature({
        ...signaturePositions[0],
        source: 'auto',
        index: 0
      });
      setCurrentPage(signaturePositions[0].page);
      Swal.fire({
        icon: 'info',
        title: 'Firmas Detectadas',
        text: `Se encontraron ${signaturePositions.length} líneas de firma automáticamente.`,
        timer: 2000,
        showConfirmButton: false
      });
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'Sin coincidencias',
        text: 'No se encontraron líneas automáticas con ese patrón. Puede ubicar la firma manualmente haciendo clic en el PDF.',
      });
    }
  };

  // Canvas interaction (Click to place/move signature)
  const handleCanvasClick = async (event) => {
    if (!pdfDoc) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const canvasX = (event.clientX - rect.left) * (canvas.width / rect.width);
    const canvasY = (event.clientY - rect.top) * (canvas.height / rect.height);

    try {
      const page = await pdfDoc.getPage(currentPage);
      const viewport = page.getViewport({ scale });
      const [pdfX, pdfY] = viewport.convertToPdfPoint(canvasX, canvasY);

      // Default signature size
      const defaultWidth = 150;
      const defaultHeight = 50;

      // Center the box on the clicked position
      const x = Math.round(pdfX - defaultWidth / 2);
      const y = Math.round(pdfY - defaultHeight / 2);

      setActiveSignature({
        page: currentPage,
        x: x < 0 ? 0 : x,
        y: y < 0 ? 0 : y,
        width: defaultWidth,
        height: defaultHeight,
        source: 'manual'
      });
    } catch (error) {
      console.error("Error al calcular coordenadas de click:", error);
    }
  };

  // Move / Nudge active signature
  const handleNudge = (direction) => {
    if (!activeSignature) return;

    let dx = 0;
    let dy = 0;

    // Note: PDF coordinate system has Y starting at the bottom and going UP.
    switch (direction) {
      case 'up':
        dy = moveStep;
        break;
      case 'down':
        dy = -moveStep;
        break;
      case 'left':
        dx = -moveStep;
        break;
      case 'right':
        dx = moveStep;
        break;
      default:
        return;
    }

    setActiveSignature(prev => ({
      ...prev,
      x: Math.max(0, prev.x + dx),
      y: Math.max(0, prev.y + dy)
    }));
  };

  // Adjust signature dimensions or values manually in input fields
  const handleActiveSigChange = (e) => {
    const { name, value } = e.target;
    setActiveSignature(prev => ({
      ...prev,
      [name]: Number(value)
    }));
  };

  const adjustDimension = (field, amount) => {
    if (!activeSignature) return;
    setActiveSignature(prev => ({
      ...prev,
      [field]: Math.max(5, prev[field] + amount)
    }));
  };

  // Save active signature to database
  const handleSaveCoordinates = async () => {
    if (!activeSignature) return;

    try {
      setSavingCoordinates(true);
      const firmasRef = collection(db, 'firmasParametros');
      const q = query(
        firmasRef,
        where("rucEmpresa", "==", ruc),
        where("tipo", "==", documentType)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const result = await Swal.fire({
          title: '¡Registro existente!',
          text: `Ya existe una firma configurada para "${documentType}". ¿Desea sobrescribirla?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, sobrescribir',
          cancelButtonText: 'No, mantener anterior',
          confirmButtonColor: '#ffc107',
          cancelButtonColor: '#6c757d'
        });

        if (!result.isConfirmed) {
          setSavingCoordinates(false);
          return;
        }

        const docId = querySnapshot.docs[0].id;
        await setDoc(doc(db, 'firmasParametros', docId), {
          cordenadaX: activeSignature.x,
          cordenadaY: activeSignature.y,
          page: activeSignature.page,
          rucEmpresa: ruc,
          tipo: documentType,
          width: activeSignature.width,
          height: activeSignature.height,
          id: docId
        });

        Swal.fire({
          icon: 'success',
          title: 'Coordenadas actualizadas',
          text: `Se actualizaron las coordenadas para ${documentType}`,
          timer: 2000,
          showConfirmButton: false
        });

      } else {
        const coordData = {
          cordenadaX: activeSignature.x,
          cordenadaY: activeSignature.y,
          page: activeSignature.page,
          rucEmpresa: ruc,
          tipo: documentType,
          width: activeSignature.width,
          height: activeSignature.height
        };

        const docRef = await addDoc(firmasRef, coordData);
        await setDoc(doc(db, 'firmasParametros', docRef.id), {
          ...coordData,
          id: docRef.id
        });

        Swal.fire({
          icon: 'success',
          title: 'Coordenadas guardadas',
          text: `Coordenadas guardadas con éxito para ${documentType}`,
          timer: 2000,
          showConfirmButton: false
        });
      }

      await fetchSavedCoordinates();
      setActiveSignature(null);

    } catch (error) {
      console.error("Error al guardar coordenadas:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron guardar las coordenadas'
      });
    } finally {
      setSavingCoordinates(false);
    }
  };

  // Delete saved coordinate
  const handleDeleteCoordinate = async (id) => {
    try {
      const result = await Swal.fire({
        title: '¿Eliminar coordenadas?',
        text: "Esta acción eliminará el registro de firma y no se puede deshacer.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d'
      });

      if (result.isConfirmed) {
        await deleteDoc(doc(db, 'firmasParametros', id));
        await fetchSavedCoordinates();
        setActiveSignature(null);
        Swal.fire('Eliminado', 'Las coordenadas han sido eliminadas', 'success');
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo eliminar el registro'
      });
    }
  };

  // Edit / Load existing saved coordinates into Active Signature
  const handleEditSavedCoordinate = (coord) => {
    setActiveSignature({
      x: coord.cordenadaX,
      y: coord.cordenadaY,
      width: coord.width || 150,
      height: coord.height || 50,
      page: coord.page || 1,
      source: 'saved',
      id: coord.id,
      tipo: coord.tipo
    });
    setDocumentType(coord.tipo);
    setCurrentPage(coord.page || 1);
  };

  // Load a detected signature into Active state for fine tuning
  const handleSelectDetectedSignature = (sig, index) => {
    setActiveSignature({
      ...sig,
      source: 'auto',
      index: index
    });
    setCurrentPage(sig.page);
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium con degradado verde oscuro */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
              borderBottom: "3px solid #f39c12",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaSignature className="me-3" />
                Detector y Administrador de Firmas
              </h2>
              <p className="mb-0 opacity-75">
                Configura y valida el posicionamiento de firmas para los documentos automatizados | RUC: {ruc}
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            
            {/* Barra superior con acciones */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1} className="d-none d-md-block text-center">
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                        color: "white"
                      }}
                    >
                      <FaSignature size={24} />
                    </div>
                  </Col>
                  <Col md={11}>
                    <h5 className="mb-1 fw-semibold">Ubicador de Firma Digital</h5>
                    <p className="text-muted mb-0 small">
                      Selecciona un documento, sube una plantilla PDF y arrastra o define con nudges las coordenadas para firma
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {initialLoading ? (
              <Card className="shadow-sm border-0 py-5">
                <Card.Body className="text-center">
                  <Spinner animation="border" variant="primary" size="lg" />
                  <p className="mt-3 text-muted">Cargando parámetros y coordenadas guardadas...</p>
                </Card.Body>
              </Card>
            ) : (
              <Row className="g-4">
                
                {/* LEFT PANEL: CONFIGURATION, ACTIVE SIGNATURE EDITOR, LISTS */}
                <Col lg={5} xl={5} className="d-flex flex-column gap-4">
                  
                  {/* Step 1: Upload & Auto Detect Settings */}
                  <Card className="shadow-sm border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
                    <Card.Header className="text-white py-3" style={{ background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)", borderBottom: "2px solid #f39c12" }}>
                      <h6 className="mb-0 fw-bold"><FaFilePdf className="me-2" /> 1. Configuración de Documento</h6>
                    </Card.Header>
                    <Card.Body className="d-flex flex-column gap-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold small text-muted">Tipo de Documento</Form.Label>
                        <Form.Select
                          value={documentType}
                          onChange={(e) => setDocumentType(e.target.value)}
                          className="border-gray rounded"
                          style={{ padding: '10px' }}
                        >
                          <optgroup label="DOCUMENTOS ESTÁNDAR">
                            {documentTypes.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </optgroup>
                          {onboardingDocs.length > 0 && (
                            <optgroup label="DOCUMENTOS DE ONBOARDING">
                              {onboardingDocs.map((doc) => (
                                <option key={doc.value} value={doc.value}>
                                  {doc.label}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </Form.Select>
                      </Form.Group>

                      <Form.Group>
                        <Form.Label className="fw-semibold small text-muted">Patrón de Búsqueda Automática</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Ejemplo: ______________"
                            value={searchPattern}
                            onChange={(e) => setSearchPattern(e.target.value)}
                            className="border-gray rounded-start"
                          />
                          <Button 
                            variant="outline-secondary"
                            onClick={() => pdfDoc && findSignatureLines(pdfDoc)}
                            disabled={!pdfDoc}
                            title="Buscar patrón en el PDF"
                          >
                            <FaSearch className="me-1" /> Buscar
                          </Button>
                        </InputGroup>
                        <Form.Text className="text-muted small">
                          Detección automática por caracteres repetidos (ej: guiones bajos).
                        </Form.Text>
                      </Form.Group>

                      <Form.Group>
                        <Form.Label className="fw-semibold small text-muted">Cargar PDF de Plantilla</Form.Label>
                        <div className="upload-container text-center border border-dashed rounded p-3 bg-light" style={{ cursor: 'pointer', position: 'relative' }}>
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={handleFileUpload}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              opacity: 0,
                              cursor: 'pointer'
                            }}
                          />
                          <FaFilePdf size={28} className="text-danger mb-2" />
                          <p className="mb-0 fw-semibold small text-dark">
                            {pdfDoc ? 'Cambiar archivo PDF' : 'Seleccionar o arrastrar PDF aquí'}
                          </p>
                          <small className="text-muted" style={{ fontSize: '11px' }}>
                            Solo archivos PDF de hasta 10MB
                          </small>
                        </div>
                      </Form.Group>
                    </Card.Body>
                  </Card>

                  {/* Step 2: Active Signature Fine-Tuning Panel */}
                  {activeSignature && (
                    <Card className="shadow-sm border-0 border-start border-4 border-success" style={{ borderRadius: "12px", overflow: "hidden" }}>
                      <Card.Header className="text-white py-3 d-flex justify-content-between align-items-center" style={{ background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)", borderBottom: "2px solid #f39c12" }}>
                        <h6 className="mb-0 fw-bold"><FaSignature className="me-2" /> 2. Parámetros de la Firma</h6>
                        <Button 
                          variant="link" 
                          className="text-white p-0 opacity-75 hover-opacity-100" 
                          onClick={() => setActiveSignature(null)}
                        >
                          <FaTimes />
                        </Button>
                      </Card.Header>
                      <Card.Body>
                        <div className="d-flex align-items-center gap-2 mb-3 bg-success bg-opacity-10 text-success p-2 rounded small">
                          <FaCrosshairs />
                          <span>
                            Ubicación activa en <strong>Página {activeSignature.page}</strong>.
                          </span>
                        </div>

                        <Row className="g-3">
                          <Col xs={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold small text-muted mb-1">Posición X (pts)</Form.Label>
                              <InputGroup size="sm">
                                <Button variant="outline-secondary" onClick={() => adjustDimension('x', -moveStep)}><FaMinus size={10}/></Button>
                                <Form.Control
                                  type="number"
                                  name="x"
                                  value={activeSignature.x}
                                  onChange={handleActiveSigChange}
                                  className="text-center"
                                />
                                <Button variant="outline-secondary" onClick={() => adjustDimension('x', moveStep)}><FaPlus size={10}/></Button>
                              </InputGroup>
                            </Form.Group>
                          </Col>
                          
                          <Col xs={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold small text-muted mb-1">Posición Y (pts)</Form.Label>
                              <InputGroup size="sm">
                                <Button variant="outline-secondary" onClick={() => adjustDimension('y', -moveStep)}><FaMinus size={10}/></Button>
                                <Form.Control
                                  type="number"
                                  name="y"
                                  value={activeSignature.y}
                                  onChange={handleActiveSigChange}
                                  className="text-center"
                                />
                                <Button variant="outline-secondary" onClick={() => adjustDimension('y', moveStep)}><FaPlus size={10}/></Button>
                              </InputGroup>
                            </Form.Group>
                          </Col>

                          <Col xs={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold small text-muted mb-1">Ancho (pts)</Form.Label>
                              <InputGroup size="sm">
                                <Button variant="outline-secondary" onClick={() => adjustDimension('width', -5)}><FaMinus size={10}/></Button>
                                <Form.Control
                                  type="number"
                                  name="width"
                                  value={activeSignature.width}
                                  onChange={handleActiveSigChange}
                                  className="text-center"
                                />
                                <Button variant="outline-secondary" onClick={() => adjustDimension('width', 5)}><FaPlus size={10}/></Button>
                              </InputGroup>
                            </Form.Group>
                          </Col>

                          <Col xs={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold small text-muted mb-1">Alto (pts)</Form.Label>
                              <InputGroup size="sm">
                                <Button variant="outline-secondary" onClick={() => adjustDimension('height', -5)}><FaMinus size={10}/></Button>
                                <Form.Control
                                  type="number"
                                  name="height"
                                  value={activeSignature.height}
                                  onChange={handleActiveSigChange}
                                  className="text-center"
                                />
                                <Button variant="outline-secondary" onClick={() => adjustDimension('height', 5)}><FaPlus size={10}/></Button>
                              </InputGroup>
                            </Form.Group>
                          </Col>
                        </Row>

                        {/* Nudge Direction Pad */}
                        <div className="d-flex flex-column align-items-center mt-3 pt-3 border-top">
                          <small className="fw-semibold text-muted mb-2">Desplazamiento Fino (PDF)</small>
                          
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            className="mb-1 rounded-circle" 
                            style={{ width: '32px', height: '32px', padding: 0 }}
                            onClick={() => handleNudge('up')}
                            title="Mover arriba (+Y)"
                          >
                            <FaArrowUp size={12}/>
                          </Button>
                          <div className="d-flex justify-content-center align-items-center">
                            <Button 
                              variant="outline-secondary" 
                              size="sm"
                              className="me-2 rounded-circle" 
                              style={{ width: '32px', height: '32px', padding: 0 }}
                              onClick={() => handleNudge('left')}
                              title="Mover izquierda (-X)"
                            >
                              <FaArrowLeft size={12}/>
                            </Button>
                            
                            {/* Step size selector in the middle */}
                            <Form.Select 
                              size="sm" 
                              value={moveStep} 
                              onChange={(e) => setMoveStep(Number(e.target.value))}
                              style={{ width: '75px', fontSize: '11px', textAlign: 'center' }}
                            >
                              <option value={1}>1 pt</option>
                              <option value={5}>5 pt</option>
                              <option value={10}>10 pt</option>
                              <option value={20}>20 pt</option>
                              <option value={50}>50 pt</option>
                            </Form.Select>

                            <Button 
                              variant="outline-secondary" 
                              size="sm"
                              className="ms-2 rounded-circle" 
                              style={{ width: '32px', height: '32px', padding: 0 }}
                              onClick={() => handleNudge('right')}
                              title="Mover derecha (+X)"
                            >
                              <FaArrowRight size={12}/>
                            </Button>
                          </div>
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            className="mt-1 rounded-circle" 
                            style={{ width: '32px', height: '32px', padding: 0 }}
                            onClick={() => handleNudge('down')}
                            title="Mover abajo (-Y)"
                          >
                            <FaArrowDown size={12}/>
                          </Button>
                        </div>

                        {/* Actions */}
                        <div className="d-grid gap-2 mt-4">
                          <Button
                            variant="success"
                            className="py-2"
                            onClick={handleSaveCoordinates}
                            disabled={savingCoordinates}
                            style={{ fontWeight: '500' }}
                          >
                            {savingCoordinates ? (
                              <><Spinner animation="border" size="sm" className="me-2" /> Guardando...</>
                            ) : (
                              <><FaSave className="me-2" /> Guardar Coordenadas para {documentType}</>
                            )}
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Auto-detected signatures list */}
                  {signatures.length > 0 && (
                    <Card className="shadow-sm border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
                      <Card.Header className="text-white py-3" style={{ background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)", borderBottom: "2px solid #f39c12" }}>
                        <h6 className="mb-0 fw-bold"><FaSearch className="me-2" /> Coordenadas Encontradas ({signatures.length})</h6>
                      </Card.Header>
                      <Card.Body className="p-0">
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          <Table hover responsive striped className="mb-0 align-middle small">
                            <thead className="table-dark sticky-top">
                              <tr>
                                <th>Pág</th>
                                <th>Coordenadas</th>
                                <th className="text-end">Acciones</th>
                              </tr>
                            </thead>
                            <tbody>
                              {signatures.map((sig, index) => {
                                const isActive = activeSignature && activeSignature.source === 'auto' && activeSignature.index === index;
                                return (
                                  <tr key={index} className={isActive ? 'table-success' : ''}>
                                    <td><Badge bg="secondary">Pág {sig.page}</Badge></td>
                                    <td>X: {sig.x}, Y: {sig.y} ({sig.width}x{sig.height})</td>
                                    <td className="text-end">
                                      <Button
                                        size="sm"
                                        variant={isActive ? 'success' : 'outline-primary'}
                                        onClick={() => handleSelectDetectedSignature(sig, index)}
                                      >
                                        {isActive ? 'Activo' : 'Seleccionar'}
                                      </Button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Saved Coordinates List */}
                  <Card className="shadow-sm border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
                    <Card.Header className="text-white py-3" style={{ background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)", borderBottom: "2px solid #f39c12" }}>
                      <h6 className="mb-0 fw-bold"><FaSave className="me-2" /> Coordenadas Registradas ({savedCoordinates.length})</h6>
                    </Card.Header>
                    <Card.Body className="p-0">
                      {savedCoordinates.length === 0 ? (
                        <div className="p-4 text-center text-muted">
                          <p className="mb-0 small">No hay coordenadas guardadas para esta empresa.</p>
                        </div>
                      ) : (
                        <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                          <Table hover responsive striped className="mb-0 align-middle small">
                            <thead className="table-dark sticky-top">
                              <tr>
                                <th>Tipo Doc</th>
                                <th>Pág</th>
                                <th>Coords (X, Y)</th>
                                <th className="text-end">Acciones</th>
                              </tr>
                            </thead>
                            <tbody>
                              {savedCoordinates.map((coord) => {
                                const isActive = activeSignature && activeSignature.source === 'saved' && activeSignature.id === coord.id;
                                return (
                                  <tr key={coord.id} className={isActive ? 'table-info' : ''}>
                                    <td className="fw-semibold">{coord.tipo}</td>
                                    <td><Badge bg="secondary">Pág {coord.page}</Badge></td>
                                    <td>X: {coord.cordenadaX}, Y: {coord.cordenadaY}</td>
                                    <td className="text-end">
                                      <div className="d-inline-flex gap-1">
                                        <Button
                                          size="sm"
                                          variant={isActive ? 'info' : 'outline-info'}
                                          onClick={() => handleEditSavedCoordinate(coord)}
                                          title="Ver/Editar firma en PDF"
                                        >
                                          Ver
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline-danger"
                                          onClick={() => handleDeleteCoordinate(coord.id)}
                                          title="Eliminar registro"
                                        >
                                          <FaTrash size={10} />
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </Card.Body>
                  </Card>

                </Col>

                {/* RIGHT PANEL: INTERACTIVE PDF VISUALIZER */}
                <Col lg={7} xl={7}>
                  <Card className="shadow-sm border-0 h-100 d-flex flex-column" style={{ borderRadius: "12px", overflow: "hidden" }}>
                    <Card.Header className="text-white py-3 d-flex flex-wrap align-items-center justify-content-between gap-3" style={{ background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)", borderBottom: "2px solid #f39c12" }}>
                      <h6 className="mb-0 d-flex align-items-center gap-2 fw-bold">
                        <FaFilePdf /> Vista Previa del Documento
                      </h6>
                      
                      {pdfDoc && (
                        <div className="d-flex align-items-center gap-2">
                          {/* Zoom controls */}
                          <div className="d-flex align-items-center border border-secondary rounded me-2 overflow-hidden bg-secondary bg-opacity-25">
                            <Button 
                              size="sm" 
                              variant="dark" 
                              className="border-0 rounded-0"
                              onClick={() => setScale(prev => Math.max(0.5, prev - 0.2))}
                              title="Reducir Zoom"
                            >
                              <FaMinus size={10} />
                            </Button>
                            <span className="px-2 text-white small" style={{ minWidth: '55px', textAlign: 'center', fontSize: '11px' }}>
                              {Math.round(scale * 100)}%
                            </span>
                            <Button 
                              size="sm" 
                              variant="dark" 
                              className="border-0 rounded-0"
                              onClick={() => setScale(prev => Math.min(3.0, prev + 0.2))}
                              title="Aumentar Zoom"
                            >
                              <FaPlus size={10} />
                            </Button>
                          </div>

                          {/* Pagination controls */}
                          <div className="d-flex align-items-center gap-1">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={currentPage === 1}
                            >
                              <FaChevronLeft size={10} />
                            </Button>
                            <span className="small px-2">
                              Página <strong>{currentPage}</strong> de {numPages}
                            </span>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setCurrentPage(prev => Math.min(numPages, prev + 1))}
                              disabled={currentPage === numPages}
                            >
                              <FaChevronRight size={10} />
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card.Header>

                    <Card.Body className="d-flex flex-column align-items-center justify-content-center p-3 bg-light" style={{ minHeight: '500px' }}>
                      {loading && (
                        <div className="text-center py-5">
                          <Spinner animation="border" variant="primary" />
                          <p className="mt-2 text-muted">Procesando y analizando estructura del PDF...</p>
                        </div>
                      )}

                      {!loading && !pdfDoc && (
                        <div className="text-center text-muted p-5 my-5 border border-dashed rounded bg-white w-75">
                          <FaFilePdf size={64} className="text-secondary mb-3 opacity-25" />
                          <h5>No hay documento cargado</h5>
                          <p className="small mb-0">Cargue un archivo PDF plantilla de la izquierda para comenzar la detección de firmas.</p>
                        </div>
                      )}

                      {!loading && pdfDoc && (
                        <div className="w-100 flex-grow-1 d-flex flex-column align-items-center">
                          <div className="alert alert-info py-2 px-3 mb-3 w-100 d-flex align-items-center gap-2 small">
                            <FaInfoCircle />
                            <span>
                              Haga clic en la página del PDF para colocar/mover la firma manualmente.
                            </span>
                          </div>

                          {/* PDF Canvas Container */}
                          <div 
                            className="shadow border rounded bg-white overflow-auto w-100 d-flex justify-content-center" 
                            style={{ 
                              maxHeight: '75vh', 
                              padding: '10px' 
                            }}
                          >
                            <canvas 
                              ref={canvasRef} 
                              onClick={handleCanvasClick}
                              style={{ 
                                cursor: 'crosshair', 
                                maxWidth: '100%', 
                                boxShadow: '0 4px 15px rgba(0,0,0,0.1)' 
                              }} 
                            />
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

              </Row>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default PDFSignatureDetector;
